import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, CheckCircle, BarChart3, Truck, Shield, Headphones, Zap } from "lucide-react";

// Eyebrow component
function Eyebrow({ children }) {
  return <span className="eyebrow">{children}</span>;
}

// Glass card component
function GlassCard({ children, className = "" }) {
  return (
    <motion.div
      className={`glass-card p-6 md:p-8 ${className}`}
      whileHover={{ y: -4, borderColor: 'rgba(86, 205, 236, 0.3)' }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
}

// Stagger reveal
function StaggerReveal({ children, index = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

const benefits = [
  { icon: BarChart3, title: "Competitive Pricing", desc: "Wholesale pricing that gives you an edge in your market. Volume discounts available." },
  { icon: Truck, title: "Fast Delivery", desc: "Multi-country warehousing ensures 3-5 day delivery to most regions globally." },
  { icon: Shield, title: "Authentic Products", desc: "100% authentic, officially licensed products from verified publisher channels." },
  { icon: Headphones, title: "Dedicated Support", desc: "Personal account manager and priority support for all partner accounts." },
  { icon: Zap, title: "Real-Time Stock", desc: "Live inventory visibility through our B2B portal. Never miss a sale." },
  { icon: CheckCircle, title: "Flexible MOQs", desc: "Minimum order quantities designed for businesses of every size." },
];

const steps = [
  { num: "01", title: "Register", desc: "Create your B2B account on our partner portal." },
  { num: "02", title: "Verify", desc: "Complete business verification and KYC process." },
  { num: "03", title: "Browse", desc: "Access our full catalog with live pricing and stock." },
  { num: "04", title: "Order", desc: "Place orders and track shipments in real-time." },
];

export default function BecomeCustomer() {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, amount: 0.3 });

  return (
    <main data-testid="become-customer-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative" ref={heroRef}>
        <div className="absolute inset-0 grid-bg opacity-20" />
        <motion.div
          className="max-w-7xl mx-auto px-fluid text-center relative z-10"
          initial={{ opacity: 0, y: 40 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Eyebrow>Partner With Us</Eyebrow>
          <h1 className="font-heading text-hero font-extrabold uppercase tracking-wider mt-4">
            Become a <span className="text-cyan">Customer</span>
          </h1>
          <p className="mt-6 text-dim text-lg max-w-2xl mx-auto">
            Access premium gaming inventory at wholesale prices.
            Join hundreds of retailers and distributors who trust Optrenex.
          </p>
          <div className="mt-8">
            <motion.a
              href="https://b2b.optrenex.com/register"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="customer-register-btn"
              className="btn-primary inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Register Now <ArrowRight size={16} />
            </motion.a>
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-panel/30">
        <div className="max-w-7xl mx-auto px-fluid">
          <div className="text-center mb-16">
            <h2 className="font-heading text-section font-bold uppercase tracking-wider">
              Why Partner With <span className="text-cyan">Optrenex</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <StaggerReveal key={i} index={i}>
                <GlassCard className="h-full" data-testid={`benefit-${i}`}>
                  <div className="w-12 h-12 rounded-lg bg-cyan-soft flex items-center justify-center mb-5">
                    <b.icon size={24} className="text-cyan" />
                  </div>
                  <h3 className="font-ui text-lg font-semibold text-ink mb-3">{b.title}</h3>
                  <p className="text-dim text-sm leading-relaxed">{b.desc}</p>
                </GlassCard>
              </StaggerReveal>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-fluid">
          <div className="text-center mb-16">
            <h2 className="font-heading text-section font-bold uppercase tracking-wider">
              How It <span className="text-cyan">Works</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <StaggerReveal key={i} index={i}>
                <div className="text-center" data-testid={`step-${i}`}>
                  <span className="font-heading text-6xl font-bold text-cyan/20">{step.num}</span>
                  <h3 className="font-ui text-lg font-semibold text-ink mt-2 mb-2">{step.title}</h3>
                  <p className="text-dim text-sm">{step.desc}</p>
                </div>
              </StaggerReveal>
            ))}
          </div>

          <div className="mt-16 text-center">
            <motion.a
              href="https://b2b.optrenex.com/register"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="customer-cta-register"
              className="btn-primary inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Get Started Today <ArrowRight size={16} />
            </motion.a>
          </div>
        </div>
      </section>
    </main>
  );
}
